/**
 * Template Builder - Itinerary Step
 * 
 * Builds day-by-day itinerary with activities
 * Allows adding/removing days and activities, editing details
 */

import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Pressable } from 'react-native';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { Ionicons } from '@expo/vector-icons';
import type { TemplateDay, TemplateActivity, TemplateActivityCategory } from '@/types/tripTemplates';

type TemplateBuilderItineraryStepProps = {
  draft: any;
  onUpdate: (updates: any) => void;
  errors: Record<string, string>;
};

const CATEGORY_OPTIONS = [
  { value: 'lodging', label: '🏨 Lodging' },
  { value: 'restaurant', label: '🍽️ Restaurant' },
  { value: 'shopping', label: '🛍️ Shopping' },
  { value: 'attraction', label: '🏛️ Attraction' },
  { value: 'activity', label: '🎯 Activity' },
  { value: 'transport', label: '🚗 Transport' },
  { value: 'flight', label: '✈️ Flight' },
  { value: 'hotel', label: '🏨 Hotel' },
  { value: 'other', label: '📍 Other' },
];

const TIME_OPTIONS = Array.from({ length: 24 * 4 }, (_, i) => {
  const hours = Math.floor(i / 4);
  const minutes = (i % 4) * 15;
  const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  return { value: timeStr, label: timeStr };
});

const getNextActivityTime = (activities: TemplateActivity[] = []) => {
  const validTimes = activities
    .map((activity) => activity.time)
    .filter((time): time is string => /^\d{2}:\d{2}$/.test(time || ''));
  const lastTime = validTimes[validTimes.length - 1];

  if (!lastTime) return '09:00';

  const [hours, minutes] = lastTime.split(':').map(Number);
  const totalMinutes = Math.min(hours * 60 + minutes + 90, 23 * 60 + 45);
  const nextHours = Math.floor(totalMinutes / 60);
  const nextMinutes = totalMinutes % 60;
  return `${nextHours.toString().padStart(2, '0')}:${nextMinutes.toString().padStart(2, '0')}`;
};

export const TemplateBuilderItineraryStep: React.FC<TemplateBuilderItineraryStepProps> = ({
  draft,
  onUpdate,
  errors,
}) => {
  const theme = useTheme();
  const [editingDay, setEditingDay] = useState<number | null>(null);
  const [editingActivity, setEditingActivity] = useState<{ dayIndex: number; activityIndex: number } | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const itinerary = draft.itinerary || [];
  const duration = draft.duration || 7;
  const activeDayIndex = itinerary.length > 0 ? Math.min(selectedDayIndex, itinerary.length - 1) : 0;
  const activeDay = itinerary[activeDayIndex];
  const totalActivities = itinerary.reduce(
    (count: number, day: TemplateDay) => count + (day.activities?.length || 0),
    0,
  );
  const daysWithActivities = itinerary.filter((day: TemplateDay) => (day.activities?.length || 0) > 0).length;

  // Track if we've initialized to avoid overwriting loaded trip data
  // Reset when itinerary changes (new trip loaded)
  const hasInitializedRef = React.useRef(false);
  const previousItineraryLengthRef = React.useRef(itinerary.length);
  
  // Reset initialization flag if itinerary length changes significantly (new trip loaded)
  React.useEffect(() => {
    if (Math.abs(itinerary.length - previousItineraryLengthRef.current) > 1) {
      hasInitializedRef.current = false;
    }
    previousItineraryLengthRef.current = itinerary.length;
  }, [itinerary.length]);

  React.useEffect(() => {
    if (itinerary.length === 0) {
      setSelectedDayIndex(0);
      return;
    }
    if (selectedDayIndex > itinerary.length - 1) {
      setSelectedDayIndex(itinerary.length - 1);
    }
  }, [itinerary.length, selectedDayIndex]);

  // Ensure we have enough days and proper numbering
  React.useEffect(() => {
    // Only run if we have a duration set
    if (!duration || duration < 1) return;
    
    // If itinerary is empty, create days from scratch
    if (itinerary.length === 0 && !hasInitializedRef.current) {
      const newDays: TemplateDay[] = [];
      for (let i = 1; i <= duration; i++) {
        newDays.push({
          day: i,
          title: `Day ${i}`,
          activities: [],
        });
      }
      onUpdate({ itinerary: newDays });
      hasInitializedRef.current = true;
      return;
    }
    
    // If we have existing days (from loaded trip data), ensure proper numbering and count
    // Only adjust if there's a mismatch
    if (itinerary.length > 0) {
      const needsRenumbering = itinerary.some((day: TemplateDay, idx: number) => day.day !== idx + 1);
      const needsMoreDays = itinerary.length < duration;
      const needsFewerDays = itinerary.length > duration;
      
      if (needsRenumbering || needsMoreDays || needsFewerDays) {
        let updated: TemplateDay[] = [...itinerary];
        
        // Remove excess days if duration decreased
        if (needsFewerDays) {
          updated = updated.slice(0, duration);
        }
        
        // Add missing days if duration increased
        if (needsMoreDays) {
          const missingDays: TemplateDay[] = [];
          for (let i = itinerary.length + 1; i <= duration; i++) {
            missingDays.push({
              day: i,
              title: `Day ${i}`,
              activities: [],
            });
          }
          updated = [...updated, ...missingDays];
        }
        
        // Renumber all days to ensure sequential numbering starting from 1
        const renumbered = updated.map((day: TemplateDay, idx: number) => ({
          ...day,
          day: idx + 1,
          // Preserve custom titles, only update if it's the default "Day X" format
          title: day.title && !day.title.match(/^Day \d+$/) ? day.title : `Day ${idx + 1}`,
        }));
        
        onUpdate({ itinerary: renumbered });
        hasInitializedRef.current = true;
      } else {
        hasInitializedRef.current = true;
      }
    }
  }, [duration, itinerary.length]); // Only depend on duration and length, not full itinerary array

  const handleAddDay = () => {
    const newDay: TemplateDay = {
      day: itinerary.length + 1,
      title: `Day ${itinerary.length + 1}`,
      activities: [],
    };
    onUpdate({ itinerary: [...itinerary, newDay] });
    setSelectedDayIndex(itinerary.length);
  };

  const handleRemoveDay = (dayIndex: number) => {
    const updated = itinerary.filter((_: any, idx: number) => idx !== dayIndex);
    // Renumber days
    const renumbered = updated.map((day: TemplateDay, idx: number) => ({
      ...day,
      day: idx + 1,
    }));
    onUpdate({ itinerary: renumbered });
    setEditingDay(null);
    setEditingActivity(null);
    setSelectedDayIndex(Math.max(0, Math.min(dayIndex, renumbered.length - 1)));
  };

  const handleUpdateDay = (dayIndex: number, updates: Partial<TemplateDay>) => {
    const updated = [...itinerary];
    updated[dayIndex] = { ...updated[dayIndex], ...updates };
    onUpdate({ itinerary: updated });
    setEditingDay(null);
  };

  const handleAddActivity = (dayIndex: number) => {
    const existingActivities = itinerary[dayIndex]?.activities || [];
    const newActivity: TemplateActivity = {
      time: getNextActivityTime(existingActivities),
      title: '',
      category: 'activity',
    };
    const updated = [...itinerary];
    updated[dayIndex] = {
      ...updated[dayIndex],
      activities: [...existingActivities, newActivity],
    };
    onUpdate({ itinerary: updated });
    setEditingActivity({ dayIndex, activityIndex: updated[dayIndex].activities.length - 1 });
  };

  const handleUpdateActivity = (
    dayIndex: number,
    activityIndex: number,
    updates: Partial<TemplateActivity>
  ) => {
    const updated = [...itinerary];
    const activities = [...(updated[dayIndex].activities || [])];
    activities[activityIndex] = { ...activities[activityIndex], ...updates };
    updated[dayIndex] = { ...updated[dayIndex], activities };
    onUpdate({ itinerary: updated });
    setEditingActivity(null);
  };

  const handleRemoveActivity = (dayIndex: number, activityIndex: number) => {
    const updated = [...itinerary];
    const activities = [...(updated[dayIndex].activities || [])];
    activities.splice(activityIndex, 1);
    updated[dayIndex] = { ...updated[dayIndex], activities };
    onUpdate({ itinerary: updated });
  };

  const handleMoveActivity = (
    dayIndex: number,
    activityIndex: number,
    direction: 'up' | 'down'
  ) => {
    const updated = [...itinerary];
    const activities = [...(updated[dayIndex].activities || [])];
    const newIndex = direction === 'up' ? activityIndex - 1 : activityIndex + 1;
    
    if (newIndex >= 0 && newIndex < activities.length) {
      [activities[activityIndex], activities[newIndex]] = [activities[newIndex], activities[activityIndex]];
      updated[dayIndex] = { ...updated[dayIndex], activities };
      onUpdate({ itinerary: updated });
    }
  };

  return (
    <ScrollView 
      style={styles.container} 
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      <Card style={styles.section}>
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <ThemedText style={styles.sectionTitle}>Itinerary Builder</ThemedText>
            <Caption color={theme.colors.textSecondary} style={{ marginTop: 4 }}>
              Build one day at a time. Select a day below to add, edit, and reorder activities.
            </Caption>
          </View>
          <Button
            label="Add Day"
            variant="secondary"
            icon={<Ionicons name="add" size={20} color={theme.colors.primary} />}
            onPress={handleAddDay}
            style={styles.addButton}
          />
        </View>

        {errors.itinerary && (
          <View style={[styles.errorContainer, { backgroundColor: theme.colors.error + '15' }]}>
            <Ionicons name="alert-circle" size={20} color={theme.colors.error} />
            <Caption color={theme.colors.error} style={styles.errorText}>
              {errors.itinerary}
            </Caption>
          </View>
        )}

        {itinerary.length === 0 && (
          <View style={styles.emptyState}>
            <Ionicons name="calendar-outline" size={48} color={theme.colors.textSecondary} />
            <Body color={theme.colors.textSecondary} style={styles.emptyText}>
              No days added yet. Click "Add Day" to start building your itinerary.
            </Body>
          </View>
        )}

        {itinerary.length > 0 && (
          <>
            <View style={[styles.itinerarySummary, { backgroundColor: theme.colors.background }]}>
              <View style={styles.summaryItem}>
                <ThemedText style={styles.summaryValue}>{itinerary.length}</ThemedText>
                <Caption color={theme.colors.textSecondary}>days</Caption>
              </View>
              <View style={styles.summaryDivider} />
              <View style={styles.summaryItem}>
                <ThemedText style={styles.summaryValue}>{totalActivities}</ThemedText>
                <Caption color={theme.colors.textSecondary}>activities</Caption>
              </View>
              <View style={styles.summaryDivider} />
              <View style={styles.summaryItem}>
                <ThemedText style={styles.summaryValue}>{daysWithActivities}/{itinerary.length}</ThemedText>
                <Caption color={theme.colors.textSecondary}>planned</Caption>
              </View>
            </View>

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.dayNavigator}
            >
              {itinerary.map((day: TemplateDay, dayIndex: number) => {
                const isSelected = dayIndex === activeDayIndex;
                const activityCount = day.activities?.length || 0;
                const hasValidationIssue =
                  !!errors[`itinerary_day_${dayIndex}_activities`] || !!errors[`itinerary_day_${dayIndex}_title`];

                return (
                  <Pressable
                    key={`day-tab-${dayIndex}`}
                    onPress={() => {
                      setSelectedDayIndex(dayIndex);
                      setEditingActivity(null);
                    }}
                    style={[
                      styles.dayTab,
                      {
                        backgroundColor: isSelected ? theme.colors.primary : theme.colors.surface,
                        borderColor: hasValidationIssue
                          ? theme.colors.error
                          : isSelected
                            ? theme.colors.primary
                            : theme.colors.borderMuted,
                      },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={`Select day ${day.day}`}
                    accessibilityState={{ selected: isSelected }}
                  >
                    <View style={styles.dayTabTopRow}>
                      <Caption
                        color={isSelected ? theme.colors.surface : theme.colors.textSecondary}
                        style={styles.dayTabEyebrow}
                      >
                        Day {day.day}
                      </Caption>
                      {hasValidationIssue && (
                        <Ionicons
                          name="alert-circle"
                          size={14}
                          color={isSelected ? theme.colors.surface : theme.colors.error}
                        />
                      )}
                    </View>
                    <ThemedText
                      numberOfLines={1}
                      style={[
                        styles.dayTabTitle,
                        { color: isSelected ? theme.colors.surface : theme.colors.text },
                      ]}
                    >
                      {day.title && day.title !== `Day ${day.day}` ? day.title : `Day ${day.day}`}
                    </ThemedText>
                    <Caption color={isSelected ? theme.colors.surface : theme.colors.textSecondary}>
                      {activityCount} {activityCount === 1 ? 'activity' : 'activities'}
                    </Caption>
                  </Pressable>
                );
              })}
            </ScrollView>
          </>
        )}

        {activeDay && (
          <View style={styles.daysContainer}>
            <Card key={activeDayIndex} style={styles.dayCard}>
              <View style={styles.dayHeader}>
                <Pressable
                  onPress={() => setEditingDay(editingDay === activeDayIndex ? null : activeDayIndex)}
                  style={styles.dayTitleButton}
                >
                  <View style={styles.dayTitleContent}>
                    <View style={[styles.dayNumberBadge, { backgroundColor: theme.colors.primary + '15' }]}>
                      <Caption color={theme.colors.primary} style={styles.dayNumberText}>
                        Day {activeDay.day}
                      </Caption>
                    </View>
                    <ThemedText style={styles.dayTitleText}>
                      {activeDay.title && activeDay.title !== `Day ${activeDay.day}` ? activeDay.title : `Day ${activeDay.day}`}
                    </ThemedText>
                    {editingDay === activeDayIndex ? (
                      <Ionicons name="chevron-up" size={20} color={theme.colors.textSecondary} />
                    ) : (
                      <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
                    )}
                  </View>
                </Pressable>
                {itinerary.length > 1 && (
                  <Pressable
                    onPress={() => handleRemoveDay(activeDayIndex)}
                    style={styles.removeButton}
                  >
                    <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
                  </Pressable>
                )}
              </View>

              <View style={styles.dayQuickActions}>
                <Button
                  label="Add activity"
                  variant="outlined"
                  size="small"
                  icon={<Ionicons name="add-circle-outline" size={18} color={theme.colors.primary} />}
                  onPress={() => handleAddActivity(activeDayIndex)}
                  style={styles.dayActionButton}
                />
                <Button
                  label={editingDay === activeDayIndex ? 'Done editing day' : 'Edit day'}
                  variant="ghost"
                  size="small"
                  icon={
                    <Ionicons
                      name={editingDay === activeDayIndex ? 'checkmark-circle-outline' : 'create-outline'}
                      size={18}
                      color={theme.colors.text}
                    />
                  }
                  onPress={() => setEditingDay(editingDay === activeDayIndex ? null : activeDayIndex)}
                  style={styles.dayActionButton}
                />
              </View>

              {(errors[`itinerary_day_${activeDayIndex}_activities`] || errors[`itinerary_day_${activeDayIndex}_title`]) && (
                <View style={[styles.inlineError, { backgroundColor: theme.colors.error + '12' }]}>
                  <Ionicons name="alert-circle-outline" size={18} color={theme.colors.error} />
                  <Caption color={theme.colors.error} style={styles.errorText}>
                    {errors[`itinerary_day_${activeDayIndex}_activities`] || errors[`itinerary_day_${activeDayIndex}_title`]}
                  </Caption>
                </View>
              )}

              {editingDay === activeDayIndex && (
                <DayEditor
                  day={activeDay}
                  onUpdate={(updates) => handleUpdateDay(activeDayIndex, updates)}
                  onClose={() => setEditingDay(null)}
                />
              )}

              <View style={styles.activitiesContainer}>
                {activeDay.activities && activeDay.activities.length > 0 ? (
                  activeDay.activities.map((activity: TemplateActivity, activityIndex: number) => (
                    <ActivityCard
                      key={activityIndex}
                      activity={activity}
                      activityIndex={activityIndex}
                      isEditing={editingActivity?.dayIndex === activeDayIndex && editingActivity?.activityIndex === activityIndex}
                      onEdit={() => setEditingActivity({ dayIndex: activeDayIndex, activityIndex })}
                      onCloseEdit={() => setEditingActivity(null)}
                      onUpdate={(updates) => handleUpdateActivity(activeDayIndex, activityIndex, updates)}
                      onRemove={() => handleRemoveActivity(activeDayIndex, activityIndex)}
                      onMove={(direction) => handleMoveActivity(activeDayIndex, activityIndex, direction)}
                      canMoveUp={activityIndex > 0}
                      canMoveDown={activityIndex < activeDay.activities.length - 1}
                    />
                  ))
                ) : (
                  <View style={styles.emptyActivities}>
                    <Caption color={theme.colors.textSecondary}>
                      No activities yet. Add one to get started.
                    </Caption>
                  </View>
                )}

                <Button
                  label="Add Activity"
                  variant="ghost"
                  icon={<Ionicons name="add-circle-outline" size={20} color={theme.colors.text} />}
                  onPress={() => handleAddActivity(activeDayIndex)}
                  style={styles.addActivityButton}
                />
              </View>
            </Card>
          </View>
        )}
      </Card>
    </ScrollView>
  );
};

// Day Editor Component
type DayEditorProps = {
  day: TemplateDay;
  onUpdate: (updates: Partial<TemplateDay>) => void;
  onClose: () => void;
};

const DayEditor: React.FC<DayEditorProps> = ({ day, onUpdate, onClose }) => {
  const theme = useTheme();
  const [title, setTitle] = useState(day.title || '');
  const [city, setCity] = useState(day.city || '');
  const [country, setCountry] = useState(day.country || '');
  const [description, setDescription] = useState(day.description || '');

  const handleSave = () => {
    onUpdate({
      title,
      description,
      city: city.trim() || undefined,
      country: country.trim() || undefined,
    });
  };

  return (
    <View style={[styles.dayEditor, { backgroundColor: theme.colors.background }]}>
      <InputField
        label="Day Title"
        placeholder="e.g., Day 1: Arrival & Myeongdong"
        value={title}
        onChangeText={setTitle}
        style={styles.editorField}
      />
      <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
        <View style={{ flex: 1 }}>
          <InputField
            label="City (optional)"
            placeholder="e.g., Tokyo"
            value={city}
            onChangeText={setCity}
            style={styles.editorField}
          />
        </View>
        <View style={{ flex: 1 }}>
          <InputField
            label="Country (optional)"
            placeholder="e.g., Japan"
            value={country}
            onChangeText={setCountry}
            style={styles.editorField}
          />
        </View>
      </View>
      <InputField
        label="Description (Optional)"
        placeholder="Brief description of this day"
        value={description}
        onChangeText={setDescription}
        multiline
        numberOfLines={2}
        style={styles.editorField}
      />
      <View style={styles.editorActions}>
        <Button label="Save" variant="primary" onPress={handleSave} style={styles.saveButton} />
        <Button label="Cancel" variant="ghost" onPress={onClose} style={styles.cancelButton} />
      </View>
    </View>
  );
};

// Activity Card Component
type ActivityCardProps = {
  activity: TemplateActivity;
  activityIndex: number;
  isEditing: boolean;
  onEdit: () => void;
  onCloseEdit: () => void;
  onUpdate: (updates: Partial<TemplateActivity>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
};

const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  isEditing,
  onEdit,
  onCloseEdit,
  onUpdate,
  onRemove,
  onMove,
  canMoveUp,
  canMoveDown,
}) => {
  const theme = useTheme();

  if (isEditing) {
    return (
      <ActivityEditor
        activity={activity}
        onUpdate={onUpdate}
        onClose={() => {
          if (!activity.title?.trim()) {
            onRemove();
          }
          onCloseEdit();
        }}
      />
    );
  }

  return (
    <Pressable onPress={onEdit} accessibilityRole="button" accessibilityLabel={`Edit ${activity.title || 'activity'}`}>
    <Card style={styles.activityCard}>
      <View style={styles.activityHeader}>
        <View style={styles.activityTime}>
          <Ionicons name="time-outline" size={16} color={theme.colors.primary} />
          <Body style={styles.timeText}>{activity.time}</Body>
        </View>
        <View style={styles.activityActions}>
          {canMoveUp && (
            <Pressable
              onPress={(event) => {
                event.stopPropagation?.();
                onMove('up');
              }}
              style={styles.moveButton}
              accessibilityRole="button"
              accessibilityLabel="Move activity up"
            >
              <Ionicons name="chevron-up" size={18} color={theme.colors.textSecondary} />
            </Pressable>
          )}
          {canMoveDown && (
            <Pressable
              onPress={(event) => {
                event.stopPropagation?.();
                onMove('down');
              }}
              style={styles.moveButton}
              accessibilityRole="button"
              accessibilityLabel="Move activity down"
            >
              <Ionicons name="chevron-down" size={18} color={theme.colors.textSecondary} />
            </Pressable>
          )}
          <Pressable
            onPress={(event) => {
              event.stopPropagation?.();
              onEdit();
            }}
            style={styles.editButton}
            accessibilityRole="button"
            accessibilityLabel="Edit activity"
          >
            <Ionicons name="pencil-outline" size={16} color={theme.colors.primary} />
            <Caption color={theme.colors.primary} style={styles.editButtonText}>Edit</Caption>
          </Pressable>
          <Pressable
            onPress={(event) => {
              event.stopPropagation?.();
              onRemove();
            }}
            style={styles.removeButton}
            accessibilityRole="button"
            accessibilityLabel="Remove activity"
          >
            <Ionicons name="trash-outline" size={18} color={theme.colors.error} />
          </Pressable>
        </View>
      </View>
      <ThemedText style={styles.activityTitle}>{activity.title || 'Untitled activity'}</ThemedText>
      {(activity.venueName || activity.location) && (
        <Caption color={theme.colors.textSecondary} style={styles.activityLocation}>
          📍 {activity.venueName || activity.location}
        </Caption>
      )}
      {(activity.geocodeHint || activity.location) && (
        <Caption color={theme.colors.textSecondary} style={styles.activityLocation}>
          🌐 {(activity.geocodeHint || activity.location) as string}
        </Caption>
      )}
      {activity.category && (
        <View style={[styles.categoryBadge, { backgroundColor: theme.colors.background }]}>
          <Caption style={styles.categoryText} color={theme.colors.textSecondary}>
            {CATEGORY_OPTIONS.find((opt) => opt.value === activity.category)?.label || activity.category}
          </Caption>
        </View>
      )}
    </Card>
    </Pressable>
  );
};

// Activity Editor Component
type ActivityEditorProps = {
  activity: TemplateActivity;
  onUpdate: (updates: Partial<TemplateActivity>) => void;
  onClose: () => void;
};

const ActivityEditor: React.FC<ActivityEditorProps> = ({ activity, onUpdate, onClose }) => {
  const theme = useTheme();
  const [title, setTitle] = useState(activity.title || '');
  const [time, setTime] = useState(activity.time || '09:00');
  const [category, setCategory] = useState(activity.category || 'activity');
  const [venueName, setVenueName] = useState(activity.venueName || '');
  const [geocodeHint, setGeocodeHint] = useState(activity.geocodeHint || activity.location || '');
  const [address, setAddress] = useState(activity.address || '');
  const [description, setDescription] = useState(activity.description || '');
  const [duration, setDuration] = useState(activity.duration?.toString() || '');
  const [estimatedCost, setEstimatedCost] = useState(activity.estimatedCost?.toString() || '');

  const handleSave = () => {
    if (!title.trim()) return;
    onUpdate({
      title: title.trim(),
      time,
      category: category as TemplateActivityCategory,
      venueName: venueName.trim() || undefined,
      geocodeHint: geocodeHint.trim() || undefined,
      // Keep legacy field in sync for older readers/migrations.
      location: geocodeHint.trim() || undefined,
      address: address || undefined,
      description: description || undefined,
      duration: duration ? parseInt(duration, 10) : undefined,
      estimatedCost: estimatedCost ? parseFloat(estimatedCost) : undefined,
    });
  };

  return (
    <Card style={[styles.activityEditor, { backgroundColor: theme.colors.surface }]}>
      <View style={styles.editorHeader}>
        <ThemedText style={styles.editorTitle}>
          {activity.title?.trim() ? 'Edit Activity' : 'Add Activity'}
        </ThemedText>
        <Pressable onPress={onClose}>
          <Ionicons name="close" size={24} color={theme.colors.text} />
        </Pressable>
      </View>

      <View style={styles.editorFields}>
        <InputField
          label="Activity Title *"
          placeholder="e.g., Visit Gyeongbokgung Palace"
          value={title}
          onChangeText={setTitle}
          style={styles.editorField}
        />

        <View style={styles.row}>
          <View style={styles.halfWidth}>
            <SelectField
              label="Time *"
              value={time}
              options={TIME_OPTIONS}
              onValueChange={setTime}
              searchable
            />
          </View>
          <View style={styles.halfWidth}>
            <SelectField
              label="Category *"
              value={category}
              options={CATEGORY_OPTIONS}
              onValueChange={(value) => setCategory(value as TemplateActivityCategory)}
            />
          </View>
        </View>

        <InputField
          label="Venue / Place Name"
          placeholder="e.g., 44th floor - Bisha Hotel"
          value={venueName}
          onChangeText={setVenueName}
          style={styles.editorField}
        />

        <InputField
          label="City / Region hint (optional)"
          placeholder="e.g., Toronto, Ontario"
          value={geocodeHint}
          onChangeText={setGeocodeHint}
          style={styles.editorField}
        />

        <InputField
          label="Address"
          placeholder="Full address"
          value={address}
          onChangeText={setAddress}
          style={styles.editorField}
        />

        <InputField
          label="Description (Optional)"
          placeholder="Activity description"
          value={description}
          onChangeText={setDescription}
          multiline
          numberOfLines={3}
          style={styles.editorField}
        />

        <View style={styles.row}>
          <View style={styles.halfWidth}>
            <InputField
              label="Duration (minutes)"
              placeholder="e.g., 120"
              value={duration}
              onChangeText={setDuration}
              keyboardType="numeric"
              style={styles.editorField}
            />
          </View>
          <View style={styles.halfWidth}>
            <InputField
              label="Estimated Cost (USD)"
              placeholder="e.g., 25"
              value={estimatedCost}
              onChangeText={setEstimatedCost}
              keyboardType="numeric"
              style={styles.editorField}
            />
          </View>
        </View>
      </View>

      <View style={styles.editorActions}>
        <Button
          label="Save Activity"
          variant="primary"
          onPress={handleSave}
          disabled={!title.trim()}
          style={styles.saveButton}
        />
        <Button label="Cancel" variant="ghost" onPress={onClose} style={styles.cancelButton} />
      </View>
    </Card>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 16,
  },
  section: {
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    gap: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    flex: 1,
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
    gap: 12,
  },
  emptyText: {
    textAlign: 'center',
  },
  itinerarySummary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 8,
    marginBottom: 14,
  },
  summaryItem: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '700',
  },
  summaryDivider: {
    width: 1,
    height: 28,
    backgroundColor: 'rgba(120,120,120,0.25)',
  },
  dayNavigator: {
    gap: 10,
    paddingBottom: 4,
  },
  dayTab: {
    width: 132,
    borderWidth: 1,
    borderRadius: 14,
    padding: 12,
    gap: 4,
  },
  dayTabTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },
  dayTabEyebrow: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  dayTabTitle: {
    fontSize: 14,
    fontWeight: '700',
  },
  daysContainer: {
    gap: 16,
    marginTop: 16,
  },
  dayCard: {
    padding: 16,
    marginBottom: 8,
  },
  dayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'transparent',
  },
  dayQuickActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  dayActionButton: {
    flexGrow: 1,
    flexBasis: 150,
  },
  dayTitleButton: {
    flex: 1,
  },
  dayTitleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  dayNumberBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
  },
  dayNumberText: {
    fontSize: 11,
    fontWeight: '700',
  },
  dayTitleText: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  removeButton: {
    padding: 8,
  },
  dayEditor: {
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
    gap: 12,
  },
  inlineError: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    borderRadius: 10,
    marginBottom: 12,
  },
  editorField: {
    marginBottom: 12,
  },
  editorActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  saveButton: {
    flex: 1,
  },
  cancelButton: {
    flex: 1,
  },
  activitiesContainer: {
    gap: 12,
    marginTop: 12,
  },
  emptyActivities: {
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  addActivityButton: {
    marginTop: 8,
  },
  activityCard: {
    padding: 14,
    borderRadius: 14,
  },
  activityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
    gap: 8,
  },
  activityTime: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  timeText: {
    fontSize: 14,
    fontWeight: '600',
  },
  activityActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 6,
    flex: 1,
  },
  moveButton: {
    padding: 4,
  },
  editButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  editButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  activityTitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  activityLocation: {
    fontSize: 12,
    marginBottom: 4,
  },
  categoryBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 4,
  },
  categoryText: {
    fontSize: 11,
  },
  activityEditor: {
    padding: 16,
    marginTop: 8,
  },
  editorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  editorTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  editorFields: {
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
});
